import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Heart, Search, User, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import { useWishlist } from "@/contexts/WishlistContext";
import { useAuth } from "@/contexts/AuthContext";
import CartDrawer from "@/components/CartDrawer";
import WishlistDrawer from "@/components/WishlistDrawer";
import CurrencySelector from "@/components/CurrencySelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const cartItems = useCartStore(state => state.totalItems());
  const { totalItems: wishlistItems } = useWishlist();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border/30 safe-top">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2 group shrink-0" onClick={() => setIsOpen(false)}>
            <span className="font-display text-xl sm:text-2xl font-semibold tracking-wider text-foreground">ALLURING</span>
            <span className="font-display text-xl sm:text-2xl font-light tracking-widest text-primary glow-text">AESTHETIC</span>
          </Link>

          <form onSubmit={handleSearch} className="hidden md:flex absolute left-1/2 -translate-x-1/2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-80 pl-10 pr-4 py-2 rounded-full bg-secondary text-foreground text-sm font-subtitle placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>
          </form>

          <div className="hidden md:flex items-center gap-2 shrink-0">
            <CurrencySelector />
            <WishlistDrawer>
              <Button variant="ghost" size="icon" aria-label="Open wishlist" className="text-muted-foreground hover:text-foreground relative h-9 w-9">
                <Heart className="h-5 w-5" />
                {wishlistItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                    {wishlistItems}
                  </span>
                )}
              </Button>
            </WishlistDrawer>
            <CartDrawer>
              <Button variant="ghost" size="icon" aria-label="Open shopping cart" className="text-muted-foreground hover:text-foreground relative h-9 w-9">
                <ShoppingBag className="h-5 w-5" />
                {cartItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                    {cartItems}
                  </span>
                )}
              </Button>
            </CartDrawer>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Account menu" className="text-muted-foreground hover:text-foreground h-9 w-9">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="text-muted-foreground text-xs font-body">
                    {user.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive font-body cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" className="ml-2">
                <Button variant="glow" size="sm" className="text-xs px-4 h-9">
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            className="md:hidden text-foreground h-11 w-11"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-background border-b border-border"
          >
            <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
              <form onSubmit={handleSearch} className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 rounded-full bg-secondary text-foreground text-sm font-body placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </form>
              <div className="flex flex-col gap-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-body text-muted-foreground">Currency</span>
                  <CurrencySelector />
                </div>
                <div className="flex items-center gap-3">
                  {user ? (
                    <Button variant="glow" className="flex-1 h-11" onClick={() => { setIsOpen(false); handleSignOut(); }}>Sign Out</Button>
                  ) : (
                    <Link to="/auth" className="flex-1" onClick={() => setIsOpen(false)}>
                      <Button variant="glow" className="w-full h-11">Sign In</Button>
                    </Link>
                  )}
                  <WishlistDrawer>
                    <Button variant="ghost" size="icon" aria-label="Open wishlist" className="relative h-11 w-11">
                      <Heart className="h-5 w-5" />
                      {wishlistItems > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                          {wishlistItems}
                        </span>
                      )}
                    </Button>
                  </WishlistDrawer>
                  <CartDrawer>
                    <Button variant="ghost" size="icon" aria-label="Open shopping cart" className="relative h-11 w-11">
                      <ShoppingBag className="h-5 w-5" />
                      {cartItems > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                          {cartItems}
                        </span>
                      )}
                    </Button>
                  </CartDrawer>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
